import { zodResolver } from '@hookform/resolvers/zod'
import { cn, getAuthClient, useAuth, useDirtyConfirm } from '@xhubio-saas/core-frontend/kernel'
import {
  Button,
  DirtyConfirmDialog,
  Input,
  Label,
  toast,
} from '@xhubio-saas/core-frontend/ui-components'
import { Info, Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { trpc } from '@/trpc'
import { FIELD_RING } from '../../pages/auth/authUi'
import { AvatarUpload } from './AvatarUpload'

/** `t` so weit das Schema es braucht — Schluessel plus Rueckfalltext. */
type Translate = (key: string, fallback: string) => string

/**
 * 🔴 Die Regeln des Profilformulars — mit **uebersetzten** Meldungen.
 *
 * Vorher stand hier `z.string().min(2, 'Name must be at least 2 characters')`.
 * Das ist die leisere Haelfte des Befunds aus Plan CORE 08-16/05: ein
 * Zod-Schema mit **eigener** `message` schlaegt jede Lokalisierung. Die
 * Uebersetzungsschicht greift nur dort, wo das Schema keine eigene Meldung
 * mitbringt — ein hartkodierter englischer Satz kommt also unveraendert beim
 * deutschen Anwender an, und zwar auch dann, wenn `noValidate` die
 * Browser-Sprechblase laengst weggenommen hat. Die Sprechblase abzuschalten
 * haette den Text nur ausgetauscht, nicht uebersetzt.
 *
 * Als Fabrik (statt als Modul-Konstante), weil `t` erst in der Komponente
 * existiert — und weil der Test die Meldungen so ohne Browser pruefen kann.
 */
export function makeProfileSchema(t: Translate) {
  return z.object({
    // `.trim()` VOR `.min()`: zwei Leerzeichen bestehen sonst jedes `min(2)`
    // (CLAUDE.md, „Drei Fallen").
    name: z.string().trim().min(2, t('profile.nameTooShort', 'Name must be at least 2 characters')),
    email: z.string().email(t('profile.emailInvalid', 'Invalid email address')),
  })
}

type ProfileFormData = z.infer<ReturnType<typeof makeProfileSchema>>

/**
 * Profile edit form
 */
export function ProfileForm() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [error, setError] = useState<string | null>(null)

  // Check if user has a social provider (Google, GitHub, Microsoft, Apple)
  const { data: profileInfo } = trpc.user.getProfile.useQuery(undefined, {
    retry: false,
  })
  const hasSocialProvider = profileInfo?.hasSocialProvider ?? false

  // Das Schema haengt an `t` — bei einem Sprachwechsel muss die Meldung mit
  // wechseln, sonst steht die alte Sprache bis zum naechsten Laden im Feld.
  const profileSchema = useMemo(() => makeProfileSchema((key, fallback) => t(key, fallback)), [t])

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    /*
      🔴 Baseline = Serverstand, durch dieselbe Abbildung wie die Anzeige
      (Hausregel `formular-echte-aenderung.md`, Satz 2). Deshalb `values` und
      nicht `defaultValues`: `user` aus `useAuth()` trifft erst ein, wenn die
      Sitzung aufgeloest ist. Mit `defaultValues` bliebe die Baseline auf dem
      Stand des ersten Renders stehen — das Formular waere entweder dauerhaft
      leer oder ab dem ersten Nachladen „geaendert", ohne dass jemand tippte.

      `keepDirtyValues`: eine neue Baseline darf die LAUFENDE Eingabe nie
      ueberschreiben. Genau das ist die verbotene Gegenbauform aus derselben
      Regel — stiller Datenverlust, waehrend jemand schreibt.

      Kanonische Form (Regel 1): leer ist `''`, nie `null`/`undefined`, damit
      ein wertgleicher zweiter Refetch nicht als Aenderung zaehlt.
    */
    values: {
      name: user?.name || '',
      email: user?.email || '',
    },
    resetOptions: { keepDirtyValues: true },
  })

  /*
    Verlassen-Schutz. `useBlocker` darunter gibt es nur im Data-Router — die App
    laeuft auf `createBrowserRouter` (core-frontend `createApp`), Tests und
    Stories deshalb ebenfalls auf einem Data-Router.
  */
  const { blocker } = useDirtyConfirm({ enabled: isDirty && !isSubmitting })

  const onSubmit = async (data: ProfileFormData) => {
    /*
      🔴 Kein Speichern-Aufruf ohne echte Aenderung (Torsten 2026-09-06). Die
      Sperre am Knopf allein reicht NICHT: `form.requestSubmit()` — Enter im
      Feld, ein Skript, ein Test — geht an einem deaktivierten Knopf vorbei.
      Beide Riegel gehoeren zusammen, der Test misst genau diesen Weg.
    */
    if (!isDirty) return

    setError(null)

    try {
      const authClient = getAuthClient()
      await authClient.updateUser({
        name: data.name,
      })
      // Neue Baseline = der gespeicherte Stand. Die Seite bleibt stehen: keine
      // Navigation, kein Moduswechsel — nur der Toast meldet den Erfolg.
      reset(data)
      toast.success(t('profile.updateSuccess', 'Profile updated successfully'))
    } catch (err) {
      setError(err instanceof Error ? err.message : t('profile.updateFailed', 'Update failed'))
    }
  }

  /**
   * Der Sprung zum Fehler (Muster `CustomerForm.tsx:404`). Ohne ihn steht die
   * rote Zeile zwar da, aber moeglicherweise ausserhalb des Sichtfelds — der
   * Anwender drueckt „Speichern" und sieht nichts passieren.
   *
   * `setTimeout(…, 0)`: erst nach dem Rendern der Fehlerzeile gibt es ein
   * `[data-error="true"]`, zu dem man springen koennte.
   */
  const onInvalid = () => {
    setTimeout(() => {
      document
        .querySelector('[data-error="true"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 0)
  }

  return (
    <div className="space-y-6">
      {/* Avatar */}
      <div className="flex items-center gap-4">
        <AvatarUpload disabled={hasSocialProvider} />
        <div>
          <p className="text-sm font-medium">{t('profile.avatar', 'Profile picture')}</p>
          <p className="text-sm text-muted-foreground">
            {hasSocialProvider
              ? t(
                  'profile.avatarDisabledSocialProvider',
                  'Profile picture is managed by your social login provider'
                )
              : t('profile.avatarHint', 'Click to upload a new picture')}
          </p>
        </div>
      </div>

      {/*
        🔴 `noValidate` — sonst ist unsere eigene Meldung UNERREICHBAR.

        Ehrliche Einordnung (gemessen, nicht angenommen): das Mailfeld unten ist
        `type="email"`, aber **immer `disabled`** — und ein deaktiviertes Feld ist
        von der Browserpruefung ausgenommen. Das Attribut repariert hier also
        heute nichts; der echte Defekt dieses Formulars war der englische
        Entwicklertext im Schema (siehe `makeProfileSchema`).

        Es steht trotzdem hier, weil „disabled" eine Eigenschaft des ZUSTANDS ist
        und nicht des Formulars: wer das Feld freischaltet — oder dem Namensfeld
        ein `required` gibt —, holt sich die Sprechblase in der Sprache des
        Browsers zurueck, und `profile.nameTooShort` laege wieder ungerendert im
        Buendel. Das Attribut kostet nichts und nimmt genau diese Falle weg
        (Plan CORE 08-16/05, Rest-Plan 08-20/03).
      */}
      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4" noValidate>
        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
        )}

        {/* The shared Input, not a hand-styled <input>: the raw version was
            white/40px/8px while every other field in the app (login, register,
            forgot) is the ui Input — two form systems in one product. */}
        <div className="space-y-2">
          <Label htmlFor="name">{t('profile.name', 'Name')}</Label>
          <Input
            id="name"
            type="text"
            {...register('name')}
            disabled={hasSocialProvider}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'profile-name-error' : undefined}
            // Ohne `data-error` AM FELD waere die rote Zeile allein sichtbar —
            // der Sprung zum Fehler (`onInvalid`) findet die Stelle erst
            // darueber (Plan CORE 08-16/05, Defektart C).
            data-error={!!errors.name || undefined}
            data-testid="profile-form-name"
            className={cn(FIELD_RING, errors.name && 'border-destructive')}
          />
          {errors.name && (
            <p
              id="profile-name-error"
              className="text-sm text-destructive"
              data-testid="profile-form-name-error"
            >
              {errors.name.message}
            </p>
          )}
          {hasSocialProvider && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Info className="h-4 w-4" />
              <span>
                {t(
                  'profile.nameDisabledSocialProvider',
                  'Name is managed by your social login provider and cannot be changed here.'
                )}
              </span>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">{t('profile.email', 'Email')}</Label>
          <Input id="email" type="email" {...register('email')} disabled />
          <p className="text-sm text-muted-foreground">
            {t('profile.emailHint', 'Email cannot be changed')}
          </p>
        </div>

        {!hasSocialProvider && (
          <Button
            type="submit"
            // 🔴 `!isDirty` gehoert HIER hin UND in den Handler — siehe dort.
            disabled={!isDirty || isSubmitting}
            data-testid="profile-form-submit"
            className={cn(
              'gap-2',
              'transition-[background-color,transform] duration-[var(--dur-micro)] ease-[var(--ease-out)]',
              'active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50'
            )}
          >
            {isSubmitting && <Loader2 aria-hidden className="h-4 w-4 animate-spin" />}
            {isSubmitting ? t('common.saving', 'Saving…') : t('common.save', 'Save')}
          </Button>
        )}
      </form>

      {/* Verlassen-Schutz fuer die Navigation innerhalb der App.
          `useBlocker` typisiert `proceed`/`reset` im entsperrten Zustand als
          optional, `DirtyConfirmDialog` ruft sie aber nur bei
          `state === 'blocked'` — deshalb ist die Umtypung sicher. Wortgleiche
          Praezedenz: `calendar-frontend/src/pages/CalendarEventDetailPage.tsx:465`. */}
      <DirtyConfirmDialog
        blocker={blocker as unknown as Parameters<typeof DirtyConfirmDialog>[0]['blocker']}
      />
    </div>
  )
}
